import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAutoGenerateQuiz } from "../hooks/useAutoGenerateQuiz";
import { useApiClient } from "../../../api";
import dayjs from "dayjs";
import { DetailsStack } from "../../../shared-components/workflows/AddDetails";
import { formatEntityType } from "../../question/utils";

export const CreateQuizDialog = ({
  open,
  setOpen,
  quizDetails = null,
  onRefresh = null,
  assignment = null,
  assignmentQuestion = null,
  skipRedirect = false,
}) => {
  const [context, setContext] = useState("");
  const [mcqCount, setMcqCount] = useState(0);
  const [detailsFromAssignment, setDetailsFromAssignment] = useState({});
  const [trueFalseCount, setTrueFalseCount] = useState(0);
  const ApiClient = useApiClient();

  const { autoGenerateQuiz } = useAutoGenerateQuiz();

  useEffect(() => {
    if (!assignment) return;

    setDetailsFromAssignment({
      id: "",
      name: `${assignment.name} - Follow Up Quiz`,
      description: `${assignment.name} - Follow Up Quiz`,
      schoolClass: assignment.schoolClass,
      subject: assignment.schoolClass.subject,
      educationLevel: assignment.schoolClass.educationLevel,
      yearGroup: assignment.schoolClass.yearGroup,
      dueDate: dayjs().add(1, "week"), // add one week
    });
  }, [assignment]);

  useEffect(() => {
    if (!open) {
      return;
    }
    // TODO: future - get assignment question feedback as well if multiple questions?
    if (!assignment?.feedback?.id && !assignmentQuestion?.feedback?.id) return;

    const subscription = ApiClient.assignment.pollOverallFeedback({
      assignmentQuestionFeedback: assignmentQuestion?.feedback?.id,
      assignmentFeedback: assignmentQuestion?.feedback?.id
        ? null
        : assignment?.feedback.id,
      onUpdate: (data) => {
        const combinedString = [
          "# Areas of improvement",
          data.specificAreasOfImprovement,
          "# What went well",
          data.feedback,
          "# Learning Evidence",
          data?.canonicalEntityInsights?.length > 0
            ? data.canonicalEntityInsights
                .map(
                  (evidence) =>
                    `- ${formatEntityType(evidence.entityType)}: ${evidence.label}
  Explanation: ${evidence.explanation}"`,
                )
                .join("\n\n")
            : "None identified",
        ]
          .filter(Boolean) // removes empty strings
          .join(". \n\n");
        setContext(combinedString);
        subscription.unsubscribe(); // stop polling after first update
      },
    });

    return () => subscription.unsubscribe();
  }, [open]);

  const handleSubmit = async () => {
    const payload = {
      number_of_mcq: mcqCount,
      number_of_true_false: trueFalseCount,
    };
    await autoGenerateQuiz({
      quizDetails: quizDetails ? quizDetails : detailsFromAssignment,
      context: context,
      payload: JSON.stringify(payload),
      onSameQuiz: onRefresh,
      files: [],
      assignment: assignment?.id,
      skipRedirect: skipRedirect,
    });

    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const canSubmit =
    context.trim().length > 0 && (mcqCount > 0 || trueFalseCount > 0);

  const handleChangeDetailsFromAssignment = (field, value) => {
    setDetailsFromAssignment({ ...detailsFromAssignment, [field]: value });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      disableScrollLock
      maxWidth={"md"}
      slotProps={{
        paper: {
          sx: {
            backgroundImage: "none",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DialogTitle>Create your own quiz</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {assignment && (
            <DetailsStack
              details={detailsFromAssignment}
              handleChange={handleChangeDetailsFromAssignment}
            />
          )}
          {/* Context */}
          <TextField
            label="Additional context"
            placeholder="e.g. strengths, weaknesses, areas to focus on…"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            required
          />

          {/* Counts */}
          <Stack direction="column" spacing={2}>
            <TextField
              label="Number of MCQs"
              type="number"
              value={mcqCount}
              onChange={(event) =>
                setMcqCount(Math.max(0, Number(event.target.value) || 0))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
            />

            <TextField
              label="Number of True / False"
              type="number"
              value={trueFalseCount}
              onChange={(event) =>
                setTrueFalseCount(Math.max(0, Number(event.target.value) || 0))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
